import React, { useState, useEffect } from "react";
import invalidField from "helpers/contact-form-validation";
import RichText from "components/rich-text";
import Image from "components/image";
import RenderField from "./data";

const ContactForm = ({ data }) => {
    const [openKey, setOpenKey] = useState(null);
    const [isSubmit, setIsSubmit] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSelected, setHasSelected] = useState(false);
    
    useEffect(() => {
        const inputChecking = () => {
            const numberFields = ["orderNumber", "year"];
    
            const addInputListener = (element) => {
                element.addEventListener("input", () => {
                    // eslint-disable-next-line no-use-before-define
                    isNumberInput(element);
                });
            };
    
            // Initially available fields
            const initialInputFields = document.querySelectorAll("input");
            initialInputFields.forEach((element) => {
                if (numberFields.includes(element.id)) {
                    addInputListener(element);
                }
            });
    
            // Listen for dynamically added fields
            const observer = new MutationObserver((mutationsList) => {
                // eslint-disable-next-line no-loops/no-loops, no-restricted-syntax
                for (const mutation of mutationsList) {
                    if (mutation.type === "childList") {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === 1 && node.tagName === "DIV") {
                                const inputs = node.querySelectorAll("input");
                                inputs.forEach((input) => {
                                    if (numberFields.includes(input.id)) {
                                        addInputListener(input);
                                    }
                                });
                            }
                        });
                    }
                }
            });
    
            observer.observe(document.getElementById("contact-form"), {
                childList: true,
                subtree: true,
            });
        };
    
        inputChecking();
    }, []);

    const isNumberInput = (element) => {
        const input = element.value;
        if (!/^[0-9]+$/.test(input)) {
            // eslint-disable-next-line no-param-reassign
            element.value = input.replace(/[^0-9]/g, "");
        }
    };    
    
    const handleSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        let isValid = true;
        const validateFields = ["email", "phoneNumber", "orderNumber", "year"];

        Array.from(event.target.elements).forEach((element) => {
            if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
                if (element.required && element.value.trim() === "") {
                    isValid = false;
                    element.classList.add("error");
                    // eslint-disable-next-line no-param-reassign
                    element.placeholder = "Please fill this field";
                } else {
                    element.classList.remove("error"); 
                    // eslint-disable-next-line no-param-reassign
                    element.placeholder = "";                   
                    if (validateFields.includes(element.id)) {
                        const { fieldValid, errorMessage } = invalidField(element.value, element.id);
                        if (!fieldValid) {
                            isValid = false;
                            element.classList.add("error");
                            // eslint-disable-next-line no-param-reassign
                            element.placeholder = errorMessage;
                            // eslint-disable-next-line no-param-reassign
                            element.value = "";
                        }
                    }
                }
            }
        });
        
        if (isValid) {
            const url = event.target.action;

            fetch(url, {
                method: "POST",
                mode: "cors",
                body: new FormData(event.target),
                redirect: "follow",
                referrerPolicy: "no-referrer",
            }).then((response) => {
                if (response.status === 200) setSubmitted(true);
            });
        }
    };

    return (
        <div className="contact-page">
            <form noValidate id="contact-form" className="overflow-x-hidden sm:overflow-visible sm:px-2" method="post" action={data?.action} encType="multipart/form-data" onSubmit={handleSubmit}>
                {data?.formItems.map((item) => (item?._type === "richText" && (
                    <div className="contact-heading" key={item?._key}>
                        <RichText value={item?.data} key={item?.key} />
                    </div>
                )))}
                <div className="sm:container relative w-[300vw] -ml-[85vw] sm:w-full py-12 sm:py-10 rounded-[50%] sm:mx-auto sm:pl-16 sm:pr-8 sm:rounded-[3.125rem] mb-10 shadow-form bg-[#f5f5f5]">
                    <div className="w-screen sm:w-full ml-[85vw] sm:mx-auto flex flex-wrap float-none px-8 pr-10 sm:px-0 py-12 sm:py-10 sm:container min-h-[56rem] content-start items-end">
                        {!submitted && data?.formItems?.map((item) => (
                            RenderField(item, openKey, setOpenKey, setIsSubmit, setHasSelected, true)
                        ))}
                        {!hasSelected && (
                            <div className="text-sm text-gray-601 italic mb-2.5">{data?.note}</div>
                        )}
                        {data?.zid && ( 
                            <>
                                <input name="zid" type="hidden" className="hidden" value={data?.zid} /> 
                                <input name="website" type="hidden" className="hidden" value="Seal Skin Covers" /> 
                            </> 
                        )} 
                        {!submitted && isSubmit && (
                            <div className="w-full">
                                {RenderField(data?.briefTextArea)}
                                <div className="w-full">
                                    <button className="contact-btn text-linkBlue" type="submit">{data?.submit}</button>
                                </div>
                            </div>
                        )}
                        {submitted && (
                            <div className="flex w-full flex-col h-[56rem] md:-ml-5">
                                <div className="relative h-[40%] flex justify-center items-center w-full">
                                    <div className="bg-[#ffe582] p-7 border-[1.875rem] border-[#fff4cd] rounded-[50%]">
                                        <div className="bg-[#ffca04] px-10 py-7 rounded-[50%]">
                                            <Image image={data?.successImage} width={100} />
                                        </div>
                                    </div>
                                    <div className="absolute bg-white border-[1.1rem] border-[#ffca04] rounded-[50%] p-2 -bottom-1 shadow-doubleCheck">
                                        <Image image={data?.successCheckImage} width={40} />
                                    </div>
                                </div>
                                <div className="h-[40%] flex flex-col justify-between items-center text-center text-blue-700">
                                    <div className="w-full max-w-lg mx-auto">
                                        <h2 className="text-h2 font-medium mb-4 leading-normal mt-5">
                                            {data?.successHeaderText}
                                        </h2>
                                    </div>
                                    <div className="my-5 -mx-5">
                                        <h4 className="text-xl mb-4 font-medium md:text-2xl">
                                            {data?.successResponseText}
                                        </h4>
                                    </div>
                                    <div className="mt-[6.25rem] relative separator -mx-3 max-w-3xl">
                                        <h5 className="text-base leading-normal font-medium md:text-lg">
                                            {data?.successInfoText}
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {data?.showImage && (
                        <div className="absolute w-32 sm:w-28 right-[42%] -bottom-10 sm:right-[10%] sm:-bottom-[5%]">
                            <Image image={data?.image} width={128} />
                        </div>
                    )}
                </div>
            </form>
        </div>
    ); 
};

export default ContactForm;
